// Profile photo upload — used by parent / teacher / admin surfaces.
// RLS on the storage bucket scopes who can write; this helper just does
// the work and updates the children row with the new path.

import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { invalidateChildAvatarCache } from "@/components/children/ChildAvatar";

const BUCKET = "child-photos";
const MAX_DIM = 512;
const MAX_BYTES = 4 * 1024 * 1024;

/**
 * Resize an image File client-side to at most 512px on the longer edge,
 * encoding to JPEG q=0.85. Keeps profile photos light and fast on mobile.
 */
async function shrink(file) {
  if (!file.type.startsWith("image/")) {
    throw new Error("Profile photo must be an image");
  }
  if (file.size > MAX_BYTES) {
    throw new Error("Image too large (max 4 MB before resize)");
  }
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Could not read image"));
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Could not decode image"));
    i.src = dataUrl;
  });
  const ratio = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const blob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85),
  );
  return blob;
}

/**
 * Upload a new profile photo for the child + atomically swap the path.
 * Returns the new storage path. Caller is responsible for refreshing
 * its local row (or refetching).
 */
export async function uploadChildProfilePhoto({ childId, file, oldPath = null }) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  if (!childId) throw new Error("Child id required");
  const blob = await shrink(file);
  const path = `profile/${childId}/${Date.now()}.jpg`;
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, {
      contentType: "image/jpeg",
      cacheControl: "3600",
      upsert: false,
    });
  if (upErr) throw upErr;
  const { error: rowErr } = await supabase
    .from("children")
    .update({ profile_photo_path: path })
    .eq("id", childId);
  if (rowErr) {
    // Roll back the orphaned object so we don't leak storage.
    try {
      await supabase.storage.from(BUCKET).remove([path]);
    } catch {}
    throw rowErr;
  }
  // Best-effort cleanup of the previous file. Fails quietly — RLS may
  // forbid teachers/parents from deleting an admin-uploaded image, and
  // that's fine: the bucket has lifecycle headroom.
  if (oldPath && oldPath.startsWith("profile/")) {
    try {
      await supabase.storage.from(BUCKET).remove([oldPath]);
    } catch {}
  }
  invalidateChildAvatarCache(oldPath);
  return path;
}

export async function clearChildProfilePhoto({ childId, oldPath }) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { error } = await supabase
    .from("children")
    .update({ profile_photo_path: null })
    .eq("id", childId);
  if (error) throw error;
  if (oldPath && oldPath.startsWith("profile/")) {
    try {
      await supabase.storage.from(BUCKET).remove([oldPath]);
    } catch {}
  }
  invalidateChildAvatarCache(oldPath);
}
